import {
  focusNextElement,
  getFirstFocusableDescendant,
  getLastFocusableDescendant
} from "../Utilities/node";

type Popover = {
  close: (focusOpenButton?: boolean) => void;
  element: Element;
  isOpen: () => boolean;
  open: () => void;
  openButton: HTMLElement;
  reposition: () => void;
};

let initialized = false;

const popovers = new Map<string, Popover>();

const initializePopover = (popoverElement: HTMLElement) => {
  let anchorElement: Element | null | undefined;

  const anchorElementId = popoverElement.dataset.anchorElementId;

  const anchorElementSelector = popoverElement.dataset.anchorElementSelector;

  if (anchorElementId) {
    anchorElement = document.getElementById(anchorElementId);
  } else if (anchorElementSelector) {
    anchorElement = document.querySelector(anchorElementSelector);
  }

  const openButton = document.querySelector<HTMLElement>(
    `[aria-controls="${popoverElement.id}"]`
  );

  const popoverContent = popoverElement.querySelector<HTMLElement>(
    "[data-popover-content]"
  );

  if (!anchorElement) {
    throw "Popover anchor element missing";
  }

  if (!openButton) {
    throw "Popover open button missing";
  }

  if (!popoverContent) {
    throw "Popover content missing";
  }

  openButton.ariaExpanded = "false";

  const positionX = popoverElement.dataset.positionX;

  const positionY = popoverElement.dataset.positionY;

  let offsetX: number;

  if (popoverElement.dataset.offsetX) {
    offsetX = parseInt(popoverElement.dataset.offsetX);
  } else {
    // Default offsetX for position left/right is equal to the margin-right of the "tip vector",
    // ie the small arrow pointing up, plus half its width.
    switch (positionX) {
      case "left":
        offsetX = 32 + 10;
        break;
      case "middle":
        offsetX = 0;
        break;
      default:
      case "right":
        offsetX = -32 - 10;
        break;
    }
  }

  let offsetY: number;

  if (popoverElement.dataset.offsetY) {
    offsetY = parseInt(popoverElement.dataset.offsetY);
  } else {
    offsetY = positionY === "below" ? 12 : -12;
  }

  const reposition = () => {
    const anchorRect = anchorElement.getBoundingClientRect();

    const popoverRect = popoverElement.getBoundingClientRect();

    const anchorMiddleX = anchorRect.left + anchorRect.width / 2;

    let left = anchorMiddleX + offsetX;

    switch (positionX) {
      case "left":
        left = left - popoverRect.width;
        break;
      case "middle":
        left = left - popoverRect.width / 2;
        break;
      default:
      case "right":
        break;
    }

    let top =
      positionY === "below"
        ? anchorRect.bottom + window.scrollY + offsetY
        : anchorRect.top - popoverRect.height + window.scrollY + offsetY;

    // Constrain to window bounds
    if (left < 0) {
      left = 0;
    } else if (left + popoverRect.width > document.body.clientWidth) {
      left = document.body.clientWidth - popoverRect.width;
    }

    if (top - window.scrollY < 0) {
      top = window.scrollY;
    } else if (top + popoverRect.height > window.innerHeight + window.scrollY) {
      top = window.innerHeight + window.scrollY - popoverRect.height;
    }

    popoverElement.style.left = `${left}px`;
    popoverElement.style.top = `${top}px`;
  };

  const closeButton = document.getElementById(`${popoverElement.id}-close-button`);

  const isOpen = () => popoverElement.inert === false;

  const closePopover = (focusOpenButton = true) => {
    if (isOpen()) {
      openButton.ariaExpanded = "false";

      popoverElement.inert = true;

      if (focusOpenButton) {
        const parent = openButton.parentElement;

        // Workaround for google markers buttons
        if (parent?.tagName === "GMP-ADVANCED-MARKER") {
          parent.focus();
        } else {
          openButton.focus();
        }
      }
    }
  };

  const openPopover = () => {
    if (!isOpen()) {
      openButton.ariaExpanded = "true";

      popoverElement.inert = false;

      // Trigger a reposition when opening, in case the user has scrolled the page since last
      // time the popover was opened.
      reposition();

      // Focus content for accessibility
      window.requestAnimationFrame(() => popoverContent.focus());
    }
  };

  // Close popover when tabbing out of it, and preserve tab order
  popoverElement.addEventListener("keydown", e => {
    if (e.key === "Tab") {
      if (e.shiftKey) {
        const child = getFirstFocusableDescendant(popoverElement);

        if (document.activeElement === child) {
          e.preventDefault();

          closePopover();
        }
      } else {
        const child = getLastFocusableDescendant(popoverElement);

        if (document.activeElement === child) {
          e.preventDefault();

          closePopover(false);

          focusNextElement(openButton);
        }
      }
    }
  });

  openButton.addEventListener("click", () => {
    if (!isOpen()) {
      openPopover();
    } else {
      closePopover();
    }
  });

  openButton.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      if (!isOpen()) {
        openPopover();
      } else {
        closePopover();
      }
    }
  });

  if (closeButton) {
    closeButton.addEventListener("click", () => {
      closePopover();
    });
  }

  reposition();

  popoverElement.dataset.popoverInitialized = "true";

  popovers.set(popoverElement.id, {
    close: closePopover,
    element: popoverElement,
    isOpen,
    open: openPopover,
    openButton,
    reposition
  });

  // Move the popover element to the document body, for absolute positioning.
  // Note: appendChild actually moves the element from one place to another.
  document.body.appendChild(popoverElement);
};

export const getPopover = (popoverElement: HTMLElement) =>
  popovers.get(popoverElement.id);

export const removePopover = (popoverElement: HTMLElement) => {
  const popoverRecord = popovers.get(popoverElement.id);

  if (popoverRecord) {
    popovers.delete(popoverElement.id);

    if (popoverRecord.isOpen()) {
      // Close and remove popover element after a delay
      popoverRecord.close();

      // Nullify attributes that can identify the popover element
      delete popoverElement.dataset.popover;
      popoverElement.id = "";

      setTimeout(() => {
        popoverElement.remove();
      }, 500);
    } else {
      popoverElement.remove();
    }
  }
};

export const initializePopovers = (rootElement: Element) => {
  if (!initialized) {
    initialized = true;

    document.addEventListener("click", event => {
      popovers.forEach(popoverRecord => {
        const { element, close, openButton } = popoverRecord;

        const isOutsidePopoverElement = element.contains(event.target as Node) === false;

        const isOutsideOpenButton =
          !openButton || openButton.contains(event.target as Node) === false;

        if (isOutsidePopoverElement && isOutsideOpenButton) {
          close();
        }
      });
    });

    document.addEventListener("keydown", event => {
      if (event.key === "Escape") {
        popovers.forEach(popoverRecord => {
          popoverRecord.close();
        });
      }
    });

    window.addEventListener("resize", () => {
      popovers.forEach(popoverRecord => {
        popoverRecord.reposition();
      });
    });
  }

  rootElement.querySelectorAll<HTMLElement>("[data-popover]").forEach(popoverElement => {
    if (!getPopover(popoverElement)) {
      initializePopover(popoverElement);
    }
  });
};

// Check for duplicate popovers after HTMX requests, and remove them.
document.addEventListener("htmx:beforeSwap", e => {
  const event = e as BeforeSwapEvent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(event.detail.serverResponse, "text/html");

  doc.querySelectorAll("[data-popover]").forEach(popover => {
    const duplicatePopover = document.getElementById(popover.id);

    if (duplicatePopover) {
      removePopover(duplicatePopover);
    }
  });
});
